import { useMemo, useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { REQUEST_PIN, SIGNUP, VALIDATE_USER, LOGIN, ME_QUERY, UPDATE_USER, VERIFY_CONTACT_CHANGE, DELETE_ACCOUNT } from '../graphql/auth';
import { getCurrentIsoWithTimezoneOffset } from '../utils/legalAcceptance';
import { getUserErrorMessage } from './useError.js';

const TOKEN_KEY = 'greenbook:token';
const CONTACT_AREAS = ['Enfermagem', 'Medicina', 'Farmácia', 'Estudante da saúde', 'Entusiasta'];
const PLANS = ['FREE', 'PLUS'];

const validateEmail = (email) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(email || '');
const validatePhone = (phone) => {
  const value = String(phone || '').trim();
  if (!value || /[A-Za-z]/.test(value) || !/^\+?[\d\s().-]+$/.test(value)) return false;

  const digits = value.replace(/\D/g, '');
  const nationalDigits = digits.startsWith('55') && digits.length >= 12
    ? digits.slice(2)
    : digits;

  return nationalDigits.length >= 10 && nationalDigits.length <= 11;
};

const GENERIC_ERROR_MESSAGE = 'Não foi possível concluir a solicitação. Tente novamente.';
const normalizeErrorMessage = getUserErrorMessage;

export const useUserProfile = () => {
  const client = useApolloClient();
  const [token, setToken] = useState(() => typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_KEY) : null);
  const [recentPin, setRecentPin] = useState(null); // { channel, value, credentialId, purpose }

  const { data, loading, refetch } = useQuery(ME_QUERY, {
    skip: !token,
    onError: () => {
       // If error (e.g. invalid token), clear token
       if (token) logout();
    }
  });

  const [requestPinMutation] = useMutation(REQUEST_PIN);
  const [signupMutation] = useMutation(SIGNUP);
  const [validateUserMutation] = useMutation(VALIDATE_USER);
  const [loginMutation] = useMutation(LOGIN);
  const [updateUserMutation] = useMutation(UPDATE_USER);
  const [verifyContactChangeMutation] = useMutation(VERIFY_CONTACT_CHANGE);
  const [deleteAccountMutation] = useMutation(DELETE_ACCOUNT);

  const profile = data?.me || null;
  const isAuthenticated = Boolean(profile);
  const isActive = profile?.status === 'active';
  const currentPlan = profile?.plan || 'FREE';
  const canUsePlus = isAuthenticated && isActive && currentPlan === 'PLUS';
  const isAdmin = profile?.role === 'admin';
  const contributionCount = profile?.contributionCount || 0;
  const plusEntitlementUntil = profile?.plusEntitlementUntil || null;
  const plusLifetime = Boolean(profile?.plusLifetime);
  const proTrialUntil = profile?.proTrialUntil || null;
  const hasWhatsapp = Boolean(profile?.whatsapp && profile?.whatsappOptIn);
  const hasAcceptedLegal = Boolean(profile?.termsAcceptedVersion && profile?.privacyAcceptedVersion);

  const logout = async () => {
    setToken(null);
    if (typeof window !== 'undefined') window.localStorage.removeItem(TOKEN_KEY);
    await client.resetStore();
  };

  const requestPin = async (channel, valueOverride, options = {}) => {
    // Determine value: override or from profile
    const value = valueOverride || (channel === 'email' ? profile?.email : profile?.phone);

    if (!value) return { success: false, message: 'Contato não informado.' };

    try {
      const res = await requestPinMutation({ variables: { channel, value } });
      if (res.data?.requestPin?.success) {
        const purpose = options.purpose || (isAuthenticated ? 'contact-change' : 'login');
        setRecentPin({ channel, value, credentialId: res.data.requestPin.credentialId, purpose });
        return { success: true, message: `PIN enviado para ${value}.`, credentialId: res.data.requestPin.credentialId };
      }
    } catch (e) {
      console.error(e);
      return { success: false, message: normalizeErrorMessage(e, 'Erro ao solicitar PIN.') };
    }
    return { success: false, message: GENERIC_ERROR_MESSAGE };
  };

  const createAccount = async (input) => {
    const errors = {};
    if (!input.fullName) errors.fullName = 'Nome é obrigatório.';
    if (!validateEmail(input.email)) errors.email = 'E-mail inválido.';
    // ... validation ...

    if (Object.keys(errors).length) return { success: false, errors };

    try {
      const preferences = {
        email: Boolean(input.allowEmail),
        phone: Boolean(input.allowPhone),
        aiMode: false
      };
      const registration = await signupMutation({
        variables: {
          fullName: input.fullName,
          birthDate: input.birthDate,
          area: input.area,
          email: input.email || null,
          phone: input.phone || null,
          plan: input.plan,
          preferences,
          legalAccepted: input.legalAccepted === true,
          legalAcceptedAtIso: getCurrentIsoWithTimezoneOffset()
        }
      });

      if (!registration.data?.signup?.success) {
        return { success: false, message: registration.data?.signup?.message || 'Erro ao cadastrar.' };
      }
      return { success: true, message: registration.data.signup.message };
    } catch (e) {
      return { success: false, message: normalizeErrorMessage(e, 'Erro ao cadastrar.') };
    }
  };

  const requestValidationPin = async (channel, valueOverride) => {
    const value = valueOverride || (channel === 'email' ? profile?.email : profile?.phone);
    if (!value) return { success: false, message: 'Contato não informado.' };

    try {
      const res = await validateUserMutation({ variables: { channel, value } });
      if (res.data?.validateUser?.success) {
        setRecentPin({ channel, value, purpose: 'validation' });
        return {
          success: true,
          message: res.data.validateUser.message || `PIN enviado para ${value}.`,
          status: res.data.validateUser.status,
          verified: res.data.validateUser.verified
        };
      }
    } catch (e) {
      console.error(e);
      return { success: false, message: normalizeErrorMessage(e, 'Erro ao solicitar PIN.') };
    }

    return { success: false, message: GENERIC_ERROR_MESSAGE };
  };

  const requestLoginPin = async ({ channel, email, phone }) => {
     const value = channel === 'email' ? email : phone;
     return requestPin(channel, value, { purpose: 'login' });
  };

  const verifyPin = async (channel, code, valueOverride) => {
    if (isAuthenticated && recentPin?.purpose === 'contact-change') {
      try {
        const res = await verifyContactChangeMutation({ variables: { code } });
        if (res.data?.verifyContactChange?.success) {
          await refetch();
          setRecentPin(null);
          return { success: true, message: 'Contato atualizado!' };
        }
      } catch (e) {
        return { success: false, message: normalizeErrorMessage(e, 'PIN inválido.') };
      }
    }

    if (recentPin?.purpose === 'validation') {
      const targetValue = valueOverride || (recentPin?.channel === channel ? recentPin.value : null);
      if (!targetValue) return { success: false, message: 'Solicite o PIN novamente.' };

      try {
        const res = await validateUserMutation({ variables: { channel, value: targetValue, code } });
        if (res.data?.validateUser?.success) {
          setRecentPin(null);
          return {
            success: true,
            message: res.data.validateUser.message || 'Contato validado.',
            status: res.data.validateUser.status,
            verified: res.data.validateUser.verified
          };
        }
      } catch (e) {
        console.error(e);
        return { success: false, message: normalizeErrorMessage(e, 'PIN inválido ou expirado.') };
      }

      return { success: false, message: GENERIC_ERROR_MESSAGE };
    }

    if (recentPin?.purpose === 'login') {
      const targetValue = recentPin?.value;
      if (!targetValue) return { success: false, message: 'Solicite o PIN novamente.' };

      try {
        const res = await loginMutation({ variables: { channel, value: targetValue, code } });
        const { token: newToken, user } = res.data?.login || {};

        if (newToken) {
          setToken(newToken);
          if (typeof window !== 'undefined') window.localStorage.setItem(TOKEN_KEY, newToken);
          await refetch();
          setRecentPin(null);
          return { success: true, profile: user };
        }
      } catch (e) {
        console.error(e);
        return { success: false, message: normalizeErrorMessage(e, 'PIN inválido ou expirado.') };
      }
    }

    return { success: false, message: 'Solicite o PIN novamente.' };
  };

  const updateProfile = async (data) => {
    try {
      await updateUserMutation({ variables: data });
      await refetch();
      return { success: true };
    } catch (e) {
      return { success: false, message: normalizeErrorMessage(e) };
    }
  };

  const switchPlan = async (plan) => {
    return updateProfile({ plan });
  };

  // Exclusão de conta: irreversível. Em caso de sucesso a sessão local é
  // encerrada imediatamente, pois o token deixa de ser aceito pela API.
  const deleteAccount = async (confirmation) => {
    try {
      const res = await deleteAccountMutation({ variables: { confirmation } });
      if (!res.data?.deleteAccount?.success) {
        return { success: false, message: res.data?.deleteAccount?.message || GENERIC_ERROR_MESSAGE };
      }
      await logout();
      return { success: true, message: res.data.deleteAccount.message };
    } catch (e) {
      return { success: false, message: normalizeErrorMessage(e, 'Erro ao excluir a conta.') };
    }
  };

  const state = useMemo(() => ({
    profile,
    isAuthenticated,
    isActive,
    currentPlan,
    canUsePlus,
    isAdmin,
    contributionCount,
    plusEntitlementUntil,
    plusLifetime,
    proTrialUntil,
    hasWhatsapp,
    hasAcceptedLegal,
    recentPin,
    loading
  }), [
    profile, isAuthenticated, isActive, currentPlan, canUsePlus, isAdmin,
    contributionCount, plusEntitlementUntil, plusLifetime, proTrialUntil,
    hasWhatsapp, hasAcceptedLegal, recentPin, loading
  ]);

  return {
    state,
    actions: {
      createAccount,
      requestPin,
      requestValidationPin,
      requestLoginPin,
      verifyPin,
      switchPlan,
      logout,
      updateProfile,
      deleteAccount,
      refetch
    }
  };
};

export const contactAreas = CONTACT_AREAS;
export const availablePlans = PLANS;
export const validateEmailFormat = validateEmail;
export const validatePhoneFormat = validatePhone;

export const formatPhoneBR = (value) => {
  const raw = String(value || '');
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
  digits = digits.slice(0, 11);

  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  const area = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${area}) ${rest}`;
  const pivot = rest.length > 8 ? 5 : 4;
  return `(${area}) ${rest.slice(0, pivot)}-${rest.slice(pivot)}`;
};

